<script setup lang="ts">
/**
 * **Area 之间的分隔条** —— 拖动改比例。
 *
 * ## 为什么做成独立组件
 *
 * 分屏的拖拽有三个容易做错的点，集中在一个人烟稀少的地方比散在
 * `NpAreaTree` 里更好：
 *
 *   1. **比例要按容器的实际尺寸算**，不能按位移像素算 ——
 *      窗口一变宽，同样的 200px 位移对应的比例就变了。
 *   2. **必须 `setPointerCapture`** —— 否则鼠标拖到画布上（那里有
 *      `touch-action: none` 和 d3-zoom）会被别人截走，表现为「拖到一半断了」。
 *   3. **拖动过程要 `user-select: none`** —— 不然会选中区域里的文字。
 *
 * ## 触摸
 *
 * 手柄的可点区域**和缝宽是两个变量**：缝宽（`--np-split-w`）进布局，
 * 恒为 2px；命中区（`--np-split-grab`）只作用于 `::before`，
 * 桌面 3px、`(pointer: coarse)` 下 9px。
 *
 * ⚠️ 用 `::before` 把命中区撑大而不是直接改宽度 ——
 * 直接改宽度会挤掉两侧 Area 的空间，比例就偏了。
 * 反过来，把**缝宽**按命中区放大（曾经就是这么写的）会在两个窗口之间
 * 留一条透出底色的空带：分割条默认透明之后它看起来像渲染坏了。
 *
 * ## 拖过头 = 合并（Blender 的 join）
 *
 * 把边界往一侧拖**超过阈值**，那一侧整个消失、另一侧吃掉它的空间。
 * 判据用的是**未夹取的原始比例** —— 布局树会把比例夹到 12%~88%，
 * 夹完就永远到不了合并区（6%），所以判定必须在夹取**之前**做。
 *
 * ⚠️ 只有那一侧是**单个 Area** 时才允许合并。「删掉这一侧」在嵌套切分里
 * 等于一口气删好几块屏 —— 拖一下边界结果三块变一块，太意外。
 * 嵌套时退化成普通夹取，这是可预期的。能不能合并由布局树的
 * `joinTarget()` 算好传进来（`joinA` / `joinB`）。
 */
import { onBeforeUnmount, ref } from 'vue'
import { JOIN_ZONE } from './areas'
import type { SplitDir } from './areas'

const props = defineProps<{
  /** `row` = 左右分（拖横向），`col` = 上下分（拖纵向） */
  dir: SplitDir
  /**
   * a 侧 / b 侧整体合并时要删掉的 Area id；`null` = 这一侧不能整体合并
   * （是嵌套切分，或者是最后一个 Area）。由布局树的 `joinTarget()` 算。
   */
  joinA?: string | null
  joinB?: string | null
}>()

const emit = defineEmits<{
  /** 拖到的新比例（0..1）。**不在这里夹取** —— 夹取规则属于布局树 */
  resize: [ratio: number]
  /** 拖过头了：把这一侧的 Area 并掉（id 来自 `joinA` / `joinB`） */
  join: [areaId: string]
}>()

const dragging = ref(false)
/** 当前是不是停在「松手就合并」的状态（用来给手柄加提示样式） */
const joinSide = ref<'a' | 'b' | null>(null)

let cleanup: (() => void) | null = null

function horizontal(): boolean {
  return props.dir === 'row'
}

/**
 * 拖动期间给 `<html>` 挂一个类，禁掉整页的文字选择。
 *
 * ⚠️ 必须挂在 `html` 上而不是手柄自己：指针捕获之后，选择行为发生在
 * 指针**下面的元素**上（也就是两侧 Area 的内容），给手柄加
 * `user-select: none` 一点用都没有。
 */
const RESIZING_CLASS = 'is-resizing-areas'

function setResizing(on: boolean) {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle(RESIZING_CLASS, on)
}

function onPointerDown(ev: PointerEvent) {
  if (ev.button !== 0 && ev.pointerType === 'mouse') return
  const el = ev.currentTarget as HTMLElement
  const container = el.parentElement
  if (!container) return

  ev.preventDefault()
  // ② 捕获指针：拖到画布上也不会被 d3-zoom / touch-action 抢走
  try {
    el.setPointerCapture(ev.pointerId)
  } catch {
    /* 某些环境不支持捕获 —— 后面 window 上的监听仍能兜住 */
  }

  const rect = container.getBoundingClientRect()
  const total = horizontal() ? rect.width : rect.height
  if (total <= 0) return

  // ① 比例按容器尺寸算：起点是容器左/上边缘，不是指针按下位置
  const origin = horizontal() ? rect.left : rect.top
  dragging.value = true
  setResizing(true)

  const onMove = (e: PointerEvent) => {
    const pos = horizontal() ? e.clientX : e.clientY
    /*
     * ★ 先算**原始**比例，再判合并，最后才交给布局树夹取。
     * 顺序反了的话永远进不了合并区（夹取后最小值就是 12%）。
     */
    const raw = (pos - origin) / total
    if (raw < JOIN_ZONE && props.joinA) joinSide.value = 'a'
    else if (raw > 1 - JOIN_ZONE && props.joinB) joinSide.value = 'b'
    else joinSide.value = null

    /* 停在合并区时**不再改比例** —— 免得先塌到 12% 再弹回去，看着很抖 */
    if (joinSide.value === null) emit('resize', raw)
  }
  const onUp = () => {
    const doomed = joinSide.value === 'a' ? props.joinA : joinSide.value === 'b' ? props.joinB : null
    dragging.value = false
    joinSide.value = null
    setResizing(false)
    try {
      el.releasePointerCapture(ev.pointerId)
    } catch {
      /* 已经释放过 */
    }
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    window.removeEventListener('pointercancel', onUp)
    cleanup = null

    /* 松手才真的合并（拖回来的话上面的 onMove 已经把 joinSide 清掉了） */
    if (doomed) emit('join', doomed)
  }

  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
  window.addEventListener('pointercancel', onUp)
  cleanup = onUp
}

/** 双击复位到 50% —— 拖歪了不用再估着拖回来 */
function onDoubleClick() {
  emit('resize', 0.5)
}

/** 键盘可达：方向键微调 2%，Home/End 走两端（夹取交给布局树） */
function onKeydown(ev: KeyboardEvent) {
  const back = horizontal() ? 'ArrowLeft' : 'ArrowUp'
  const fwd = horizontal() ? 'ArrowRight' : 'ArrowDown'
  const current = Number((ev.currentTarget as HTMLElement).dataset.ratio ?? '0.5')
  const step = ev.shiftKey ? 0.1 : 0.02

  if (ev.key === back) emit('resize', current - step)
  else if (ev.key === fwd) emit('resize', current + step)
  else if (ev.key === 'Home') emit('resize', 0)
  else if (ev.key === 'End') emit('resize', 1)
  else return
  ev.preventDefault()
}

onBeforeUnmount(() => cleanup?.())
</script>

<template>
  <div
    class="np-split-handle"
    :class="[
      `np-split-handle--${dir}`,
      { 'is-dragging': dragging, 'is-joining': joinSide !== null },
    ]"
    role="separator"
    tabindex="0"
    :aria-orientation="dir === 'row' ? 'vertical' : 'horizontal'"
    :title="
      joinA || joinB
        ? '拖动调整比例 · 拖到一端可合并 · 双击复位 50%'
        : '拖动调整比例 · 双击复位 50%'
    "
    @pointerdown="onPointerDown"
    @dblclick="onDoubleClick"
    @keydown="onKeydown"
  />
</template>

<style scoped>
.np-split-handle {
  /*
   * ⚠️ 宽度读的是父级给的 `--np-split-w`（不是主题那个 `--np-split-size`）——
   * 父级已经按主题 + 触摸端下限算好了最终值，这里再读一次原始值
   * 会把「触摸端至少 12px」那条规则绕过。
   * 父级（`.np-split`）还要拿这个值的一半去补偿两侧 pane 的 flex-basis，
   * 两处必须来自同一个数 —— 各写一份迟早会漂（这个仓库里
   * 「小地图 200×130 被抄进五个地方」就是同一个病）。
   * 触摸端的放大也归父级的 `(pointer: coarse)` 媒体查询管。
   */
  position: relative;
  flex: 0 0 var(--np-split-w, 6px);
  align-self: stretch;
  /*
   * ⚠️ **默认透明**。
   *
   * 原来是 `var(--np-border-bar)` —— 一条贯穿整屏的实心深色带子。
   * 两个窗口之间本来就有一道底色差，再画一条实心分隔线是**双重的边界**，
   * 而且它在深色主题下比两侧都暗，看着像一道裂缝。
   *
   * 现在是「**存在但不显形**」：宽度还在（`flex: 0 0 …`，两侧比例靠它算），
   * 命中区还在（`::before` 撑大），只有 hover / 拖拽 / 合并预览时才亮起来。
   * 也就是说**它从一个装饰变成了一个控件**。
   */
  background: transparent;
  touch-action: none;
  /* ⚠️ 时长走令牌（理由同 theme.css 的动效那一段） */
  transition: background-color var(--np-motion-fast) var(--np-ease);
}

/*
 * 命中区撑大：**不改变布局宽度**，否则两侧比例会偏。
 * 撑开量来自 `--np-split-grab`（桌面 3px → 8px 命中区；触摸 9px → 20px）。
 */
.np-split-handle::before {
  content: '';
  position: absolute;
  inset: calc(var(--np-split-grab, 3px) * -1);
}

.np-split-handle--row {
  cursor: col-resize;
}

.np-split-handle--col {
  cursor: row-resize;
}

.np-split-handle:hover,
.np-split-handle:focus-visible,
/*
 * 停在合并区：手柄变强调色 + 加粗，明确「松手就会并掉这一侧」。
 * ⚠️ 这个反馈是**必需**的，不是装饰 —— 合并是不可逆的结构操作
 * （虽然能撤销），没有预览的话用户不知道松手会发生什么。
 */
.np-split-handle.is-joining {
  background: var(--np-accent, #f0a04b);
}

.np-split-handle.is-dragging {
  background: var(--np-accent, #f0a04b);
  outline: none;
}

/*
 * ③ 拖动期间禁掉文字选择。
 * ⚠️ 挂在 `html` 上而不是手柄自己：指针捕获之后，选择行为发生在
 * 指针下面的元素上（也就是两侧的 Area 内容），给手柄加 `user-select`
 * 一点用都没有。
 */
:global(html.is-resizing-areas),
:global(html.is-resizing-areas *) {
  user-select: none !important;
}

/*
 * ⚠️ 触摸端的宽度放大在 `NpAreaTree` 的 `.np-split` 上（`--np-split-size`），
 * 这里**不再写第二份** —— 见上面 `.np-split-handle` 的注释。
 */
</style>
